import React, { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { formatTimestampForZone2 } from "../utils/formattimestamp";

const CallActivityGraph = ({ call_data, dateRange, frequency, isLoading = false, startDate: customStartDate = null, endDate: customEndDate = null, hideHeader = false, strokeColor = "#8B5CF6" }) => {
  // Don't render chart if data is still loading
  const hasData = call_data && call_data.length > 0 && !isLoading;
  const gradientId = `volumeGradient-${String(strokeColor).replace("#", "")}`;

  // Transform the call_data to group by selected frequency
  const processedData = useMemo(() => {
    if (!call_data || call_data.length === 0) return [];
    const now = customEndDate ? new Date(customEndDate) : new Date();
    const startDate = customStartDate ? new Date(customStartDate) : new Date();

    // Use custom dates if provided, otherwise calculate from dateRange
    if (!customStartDate) {
      if (dateRange === 1) {
        // Last 24 hours
        startDate.setDate(now.getDate() - 1);
      } else {
        startDate.setDate(now.getDate() - (dateRange || 30));
      }
    }

    const groupedData = {};

    // Step 1: Group call data by daily or hourly keys
    call_data.forEach((call) => {
      if (!call.callTime || call.callTime === "N/A") return;
      const callTimeStr = String(call.callTime);
      let date;
      if (callTimeStr.length === 10) {
        date = formatTimestampForZone2(Number(call.callTime) * 1000);
      } else if (callTimeStr.length === 13) {
        date = formatTimestampForZone2(parseInt(Number(call.callTime), 10));
      } else {
        return;
      }

      let callDate = date;

      // Filter calls outside the date range
      if (callDate < startDate || callDate > now) return;

      let key;
      if (frequency === "hourly") {
        key = callDate.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
        });
      } else {
        key = callDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      }

      if (!groupedData[key]) {
        groupedData[key] = 0;
      }
      groupedData[key] += 1; // Increment count for each call
    });

    const allDates = [];
    let currentDate = new Date(startDate);

    // Step 2: Generate data based on the frequency
    if (frequency === "weekly") {
      // Weekly aggregation
      while (currentDate <= now) {
        const weekStart = new Date(currentDate);
        weekStart.setDate(currentDate.getDate() - currentDate.getDay()); // Start of the week (Sunday)
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // End of the week (Saturday)

        const weekKey = `${weekStart.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })} - ${weekEnd.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })}`;

        // Aggregate weekly count
        let weeklyCount = 0;
        for (
          let d = new Date(weekStart);
          d <= weekEnd;
          d.setDate(d.getDate() + 1)
        ) {
          const dailyKey = d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
          weeklyCount += groupedData[dailyKey] || 0;
        }

        allDates.push({ date: weekKey, volume: weeklyCount });
        currentDate = new Date(weekEnd); // Move to the end of the week
        currentDate.setDate(currentDate.getDate() + 1); // Increment to the next day
      }
    } else if (frequency === "hourly") {
      // Hourly aggregation
      while (currentDate <= now) {
        const dayStart = new Date(currentDate);
        for (let hour = 0; hour < 24; hour++) {
          const hourlyKey = new Date(
            dayStart.setHours(hour, 0, 0, 0)
          ).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
          });

        allDates.push({
          date: hourlyKey,
          volume: groupedData[hourlyKey] || 0, // Use grouped data if available, else 0
        });
        }
        currentDate.setDate(currentDate.getDate() + 1); // Increment day
      }
    } else {
      // Daily aggregation
      while (currentDate <= now) {
        const dailyKey = currentDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });

        allDates.push({
          date: dailyKey,
          volume: groupedData[dailyKey] || 0, // Use grouped data if available, else 0
        });

        currentDate.setDate(currentDate.getDate() + 1); // Increment day
      }
    }

    return allDates;
  }, [call_data, dateRange, frequency, customStartDate, customEndDate]);

  const rangeLabel =
    customStartDate && customEndDate
      ? `${new Date(customStartDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${new Date(customEndDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
      : `Last ${dateRange === 1 ? "24 Hours" : `${dateRange} Days`}`;

  // Don't render chart until data is available (not loading and has data)
  if (isLoading || !hasData) {
    return (
      <div className="w-full h-full">
        {!hideHeader && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-800">Call Volume</h3>
            <div className="text-sm text-gray-500 capitalize">
              {rangeLabel} • {frequency}
            </div>
          </div>
        )}
        {hideHeader && (
          <div className="text-sm text-gray-500 capitalize mb-2">{rangeLabel} • {frequency}</div>
        )}
        <div className="h-72 flex items-center justify-center">
          {isLoading ? (
            <div className="text-gray-400 text-sm">Loading chart data...</div>
          ) : (
            <div className="text-gray-400 text-sm">No call data available</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      {!hideHeader && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-800">Call Volume</h3>
          <div className="text-sm text-gray-500 capitalize">
            {rangeLabel} • {frequency}
          </div>
        </div>
      )}
      {hideHeader && (
        <div className="text-sm text-gray-500 capitalize mb-2">
          {rangeLabel} • {frequency}
        </div>
      )}
      <div className={hideHeader ? "h-[300px]" : "h-72"}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={processedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={strokeColor} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={strokeColor} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              allowDecimals={false}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="volume" 
              stroke={strokeColor}
              strokeWidth={3}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 6, fill: strokeColor, strokeWidth: 2, stroke: '#FFFFFF' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CallActivityGraph;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: '#1F2937',
          color: '#F9FAFB',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          border: 'none',
          padding: '8px 12px',
        }}
      >
        <p style={{ color: '#F9FAFB', marginBottom: '4px', fontSize: '12px' }}>{label}</p>
        <p style={{ color: '#F9FAFB', fontSize: '14px', fontWeight: '600' }}>
          {payload[0]?.value}
        </p>
      </div>
    );
  }

  return null;
};
